import React, { useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { Button } from '@oforce/global-components';

import { useDownloadFile } from '../../hooks';
import { useGenerateInvoicesReportJobContext } from '../../context';
import { ToastContent } from '../../components/react-toastify';

export default function GenerateInvoicesReportJob() {
  const { jobId, message, finished, filename, url, error, jobErrors, clearJobId } =
    useGenerateInvoicesReportJobContext();

  const toastId = useRef(null);

  useEffect(() => {
    if (!toast.current && jobId) {
      toastId.current = toast(<GeneratingInvoicesReport />, { autoClose: false, closeOnClick: false });
    }
  }, [toastId, jobId, clearJobId]);

  useEffect(() => {
    if (!toastId.current) {
      return;
    }

    if (error || jobErrors.length !== 0) {
      toast.update(toastId.current, {
        render: () => <ErrorGeneratingInvoicesReport error={jobErrors || error} />,
        onClose: clearJobId
      });
    } else if (!finished && !url) {
      toast.update(toastId.current, {
        render: () => <GeneratingInvoicesReport message={message} />
      });
    } else if (finished) {
      toast.update(toastId.current, {
        render: () => (
          <DownloadInvoicesReport
            message={message}
            url={url}
            filename={filename}
            errors={jobErrors}
            clearJobId={clearJobId}
          />
        ),
        onClose: clearJobId
      });
    }
    // eslint-disable-next-line
  }, [toastId, message, filename, url, finished, error, jobErrors]);

  return <></>;
}

const GeneratingInvoicesReport = ({ message }) => {
  let title = message ?? 'Preparing Report';

  return (
    <ToastContent
      title={title}
      icon={['far', 'spinner-third']}
      iconProps={{ spin: true }}
      message="Please allow us some time to generate the report."
    />
  );
};

const ErrorGeneratingInvoicesReport = ({ error }) => (
  <ToastContent
    title="Error Preparing Report"
    titleProps={{ className: 'text-danger' }}
    icon={['fas', 'times-circle']}
    message={error.message || error}
  />
);

const DownloadInvoicesReport = ({ message, url, filename, errors, clearJobId }) => {
  const { download, loading } = useDownloadFile({ url, filename });

  const downloadAndClearJobId = () => {
    download();
    clearJobId();
  };

  return (
    <ToastContent
      title={message || 'Report Ready'}
      titleProps={{ className: 'text-success' }}
      icon={url ? ['fas', 'check-circle'] : ['far', 'exclamation-triangle']}
      message={url && 'Your report is ready for download.'}
    >
      {errors && errors.length > 0 && errors.map(e => <div key={e}>{e}</div>)}
      <div>
        {url && (
          <Button
            color="link"
            size="sm"
            icon={['far', 'arrow-to-bottom']}
            className="mr-2"
            loading={loading}
            loadingText="Downloading..."
            onClick={downloadAndClearJobId}
          >
            Download Now
          </Button>
        )}
      </div>
    </ToastContent>
  );
};
